'use client';
import useAvailableAppointmentsModal from '@/hooks/useAvailableAppointmentsModal';
import { isNil } from 'lodash-es';
import type React from 'react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { formatDay } from '../../../common/formatter';
import useContactConfirmationModal from '../../../hooks/useContactConfirmationModal';
import useLoginModal from '../../../hooks/useLoginModal';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import type { CartSelectionType } from '../../../redux/ducks/CartDucks.types';
import Button from '../../button/Button';

interface PartnerSeoPageProps {
  currentCart: CartSelectionType | undefined;
}

const PartnerSeoMobileFooter: React.FC<PartnerSeoPageProps> = ({
  currentCart,
}) => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const confirmContactModal = useContactConfirmationModal();
  const availableAppointmentsModal = useAvailableAppointmentsModal();
  const loginModal = useLoginModal();
  const { isLoggedIn } = useUser();

  const [onClient, setOnClient] = useState<boolean>(false);

  useEffect(() => {
    setOnClient(true);
  }, []);

  const onContact = useCallback(() => {
    if (isLoggedIn) {
      confirmContactModal.onOpen();
    } else {
      loginModal.onOpen();
    }
  }, [isLoggedIn, loginModal.onOpen, confirmContactModal.onOpen]);

  const formattedDay = useMemo<string>(() => {
    if (isNil(currentCart)) {
      return '';
    }

    return formatDay({
      timestamp: currentCart.from,
      timezone: currentTimezone,
      language: i18n.language,
      t,
      shortDay: false,
      shortMonth: true,
      noYear: true,
    });
  }, [currentCart, t, i18n, currentTimezone]);

  const formattedTimeWindow = useMemo<string>(() => {
    if (isNil(currentCart)) {
      return '';
    }

    return `${Intl.DateTimeFormat(i18n.language, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      timeZone: currentTimezone,
    }).format(new Date(currentCart.from))}
             · 
            ${Intl.DateTimeFormat(i18n.language, {
              hour: 'numeric',
              minute: 'numeric',
              hour12: false,
              timeZone: currentTimezone,
            }).format(new Date(currentCart.to))}`;
  }, [currentCart, i18n, currentTimezone]);

  const unavailableCartBody = useMemo(
    () => (
      <button
        type="button"
        onClick={availableAppointmentsModal.onOpen}
        className="cursor-pointer min-w-full min-h-[64px] rounded-t-xl border-t-2 border-solid border-amber-200 bg-amber-100 p-2 flex flex-col items-center justify-center  hover:bg-sky-600 hover:text-white group"
      >
        <span className="flex min-h-full flex-col items-center justify-center">
          <span className="min-w-full text-sm p-2 font-bold">
            <Trans i18nKey="static.cart.no_slots_available_mobile_description">
              <span className="font-bold">&nbsp;</span>
            </Trans>
          </span>
        </span>
      </button>
    ),
    [availableAppointmentsModal],
  );

  return (
    <div className="w-screen">
      {currentCart ? (
        <div className="min-w-full min-h-[64px] rounded-t-xl border-t-2 border-solid border-slate-200 bg-white p-2 flex flex-col items-center justify-center">
          <div className="flex flex-row items-center justify-between gap-2 px-2 min-w-full">
            <div className="grow">
              <div className="flex w-full flex-col items-start justify-start">
                <span className="flex flex-col items-start justify-center gap-0 text-sm capitalize">
                  <div className="flex flex-col items-start justify-start gap-0">
                    <span className="line-clamp-1 w-full overflow-hidden text-ellipsis break-all font-bold">
                      {currentCart?.service.name}
                    </span>
                  </div>
                  {onClient && (
                    <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                      <span className="line-clamp-1 w-full grow overflow-hidden text-ellipsis break-all">
                        {formattedDay}, {formattedTimeWindow}
                      </span>
                    </Suspense>
                  )}
                  <span className="line-clamp-1 w-full grow overflow-hidden text-ellipsis break-all">
                    {currentCart?.business.name
                      ? `${currentCart?.business.name}, ${currentCart?.business.fullAddress}`
                      : currentCart?.business.fullAddress}
                  </span>
                </span>
              </div>
            </div>
            <div className="min-w-[128px] items-center justify-center">
              <Button
                disabled={false}
                loading={false}
                label={t('static.cart.appointment_action_button_short_label')}
                onClick={onContact}
                data-test-id="mobile-cart-contact-button"
                id="mobile-cart-contact-button"
              />
            </div>
          </div>
        </div>
      ) : (
        unavailableCartBody
      )}
    </div>
  );
};

export default PartnerSeoMobileFooter;
